import { RELEASE_GATE_V2_13_0 } from './releaseStatsigGateConstants.js'

const TENANT_FEATURE_CATALOG_STORAGE_KEY = 'features'

export const DYNAMIC_HEADER_NAV_LABELS_GATE = RELEASE_GATE_V2_13_0

export const parseTenantFeatureCatalogFromStorage = () => {
  if (typeof localStorage === 'undefined') {
    return []
  }

  const raw = localStorage.getItem(TENANT_FEATURE_CATALOG_STORAGE_KEY)
  if (!raw) {
    return []
  }

  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const mergeFeatureCatalogs = (primaryCatalog = [], fallbackCatalog = []) => {
  const seenFeatureKeys = new Set()
  const mergedGroups = []

  const appendCatalog = (catalog) => {
    ;(catalog || []).forEach((group) => {
      const features = (group?.features || []).filter((feature) => {
        const featureKey = feature?.key
        if (!featureKey || seenFeatureKeys.has(featureKey)) {
          return false
        }

        seenFeatureKeys.add(featureKey)
        return true
      })

      if (features.length > 0) {
        mergedGroups.push({ ...group, features })
      }
    })
  }

  appendCatalog(primaryCatalog)
  appendCatalog(fallbackCatalog)

  return mergedGroups
}

export const resolveHeaderFeatureCatalog = (featureDefinitions = []) => {
  const tenantCatalog = parseTenantFeatureCatalogFromStorage()
  const apiCatalog = Array.isArray(featureDefinitions) ? featureDefinitions : []

  if (apiCatalog.length === 0) {
    return tenantCatalog
  }

  return mergeFeatureCatalogs(apiCatalog, tenantCatalog)
}

export const buildHeaderNavLabelMaps = (featureDefinitions = []) => {
  const parentByFeatureKey = {}
  const childByFeatureKey = {}

  featureDefinitions.forEach((group) => {
    const parentName = String(group?.name || '').trim()

    ;(group?.features || []).forEach((feature) => {
      const featureKey = feature?.key
      if (!featureKey) {
        return
      }

      parentByFeatureKey[featureKey] = parentName
      childByFeatureKey[featureKey] = String(feature?.title || feature?.name || parentName).trim()
    })
  })

  return { parentByFeatureKey, childByFeatureKey }
}

export const getParentNavLabel = (featureKey, maps = {}, fallback = '') => {
  if (!featureKey) {
    return fallback
  }

  return maps?.parentByFeatureKey?.[featureKey] || fallback
}

export const getChildNavLabel = (featureKey, maps = {}, fallback = '') => {
  if (!featureKey) {
    return fallback
  }

  return maps?.childByFeatureKey?.[featureKey] || fallback
}

const DEFAULT_LIVE_SHIPMENTS_LABEL = 'Live Shipments'
const DEFAULT_COMPLETED_SHIPMENTS_LABEL = 'Completed Shipments'
const DEFAULT_TENANT_ONBOARDING_LABEL = 'Tenant Onboarding'
const DEFAULT_ORGANIZATION_MANAGEMENT_LABEL = 'Organization Management'

export const buildLegacyHeaderNavLabels = (menuList = {}) => ({
  addNewShipment: menuList.add_new_shipment,
  inbox: menuList.inbox,
  commandCenter: menuList.command_center,
  temperatureRelease: menuList.temperature_release,
  qualificationSandbox: 'Qualification Sandbox',
  athenaSandbox: menuList.athena_sandbox,
  insights: 'Insights',
  insightsCommandCenter: 'Command Center',
  insightsLanePerformance: menuList.lane_performance,
  insightsTemperatureRelease: menuList.temperature_release,
  automation: menuList.automation,
  workflows: 'Workflows',
  configuration: menuList.configuration,
  productProfile: 'Product Profile',
  laneProfile: 'Lane Profile',
  tags: 'Tags',
  manage: menuList.manage,
  organization: 'Organization',
  userGroup: 'User Group',
  adminSettings: menuList.app_config,
  tenantOnboarding: DEFAULT_TENANT_ONBOARDING_LABEL,
  organizationManagement: DEFAULT_ORGANIZATION_MANAGEMENT_LABEL,
  featureManagement: 'Feature Management',
  apiVersions: 'API Versions',
  tenant: 'Tenant',
  manageOrganizations: DEFAULT_ORGANIZATION_MANAGEMENT_LABEL,
  help: menuList.help,
  liveShipments: 'Live Shipments',
  completedShipments: 'Completed Shipments',
  requestNewLane: 'Request New Lane',
  dashboard: 'Dashboard',
  sla: 'SLA',
})

export const buildDynamicHeaderNavLabels = (navLabelMaps = {}, menuList = {}) => ({
  addNewShipment: menuList.add_new_shipment,
  inbox: menuList.inbox,
  commandCenter: getParentNavLabel('commandcenter', navLabelMaps, menuList.command_center),
  temperatureRelease: getParentNavLabel('temprelease', navLabelMaps, menuList.temperature_release),
  qualificationSandbox: getParentNavLabel(
    'qualification_sandbox',
    navLabelMaps,
    'Qualification Sandbox',
  ),
  athenaSandbox: getParentNavLabel('athena_sandbox', navLabelMaps, menuList.athena_sandbox),
  insights: getParentNavLabel('insights_commandcenter', navLabelMaps, 'Insights'),
  insightsCommandCenter: getChildNavLabel('insights_commandcenter', navLabelMaps, 'Command Center'),
  insightsLanePerformance: getChildNavLabel(
    'insights_laneperformance',
    navLabelMaps,
    menuList.lane_performance,
  ),
  insightsTemperatureRelease: getChildNavLabel(
    'insights_temprelease',
    navLabelMaps,
    menuList.temperature_release,
  ),
  automation: getParentNavLabel('automation_workflows', navLabelMaps, menuList.automation),
  workflows: getChildNavLabel('automation_workflows', navLabelMaps, 'Workflows'),
  configuration: getParentNavLabel('product_profiles', navLabelMaps, menuList.configuration),
  productProfile: getChildNavLabel('product_profiles', navLabelMaps, 'Product Profile'),
  laneProfile: getChildNavLabel('lane_profiles', navLabelMaps, 'Lane Profile'),
  tags: getChildNavLabel('tags', navLabelMaps, 'Tags'),
  manage: menuList.manage,
  organization: getChildNavLabel('org_settings', navLabelMaps, 'Organization'),
  userGroup: getChildNavLabel('member', navLabelMaps, 'User Group'),
  adminSettings: getParentNavLabel('feature_flag_management', navLabelMaps, menuList.app_config),
  tenantOnboarding: getChildNavLabel(
    'tenant_dashboard',
    navLabelMaps,
    DEFAULT_TENANT_ONBOARDING_LABEL,
  ),
  organizationManagement: getChildNavLabel(
    'manage_organization',
    navLabelMaps,
    DEFAULT_ORGANIZATION_MANAGEMENT_LABEL,
  ),
  featureManagement: getChildNavLabel(
    'feature_flag_management',
    navLabelMaps,
    'Feature Management',
  ),
  apiVersions: getChildNavLabel('api_config', navLabelMaps, 'API Versions'),
  tenant: getParentNavLabel('manage_organization', navLabelMaps, 'Tenant'),
  manageOrganizations: getChildNavLabel(
    'manage_organization',
    navLabelMaps,
    DEFAULT_ORGANIZATION_MANAGEMENT_LABEL,
  ),
  help: menuList.help,
  liveShipments: getChildNavLabel('commandcenter_live', navLabelMaps, DEFAULT_LIVE_SHIPMENTS_LABEL),
  completedShipments: getChildNavLabel(
    'commandcenter_completed',
    navLabelMaps,
    DEFAULT_COMPLETED_SHIPMENTS_LABEL,
  ),
  requestNewLane: 'Request New Lane',
  dashboard: 'Dashboard',
  sla: 'SLA',
})

export const resolveHeaderNavLabels = ({
  useDynamicNavLabels = false,
  navLabelMaps = {},
  menuList = {},
} = {}) =>
  useDynamicNavLabels
    ? buildDynamicHeaderNavLabels(navLabelMaps, menuList)
    : buildLegacyHeaderNavLabels(menuList)
